import { Injectable } from '@angular/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { ConfigService } from '@shared/services/config.service';
import { Observable, ReplaySubject, catchError, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';

export interface EUAV3UserInfo {
  lastName: string;
  sub: string;
  manager: string;
  displayName: string;
  groups: string[];
  trust_level: string;
  userId: string;
  employeeNumber: string;
  firstName: string;
  employeeType: string;
  scope: string[];
  realm: string;
  shortName: string;
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Azure V1 auth response
  private readonly azureV1AuthSubject$: ReplaySubject<LoginResponse> = new ReplaySubject<LoginResponse>();
  private readonly azureV1Auth$: Observable<LoginResponse> = this.azureV1AuthSubject$.asObservable();

  // Azure V2 auth response
  private readonly azureV2AuthSubject$: ReplaySubject<LoginResponse> = new ReplaySubject<LoginResponse>(1);
  public readonly azureV2Auth$: Observable<LoginResponse> = this.azureV2AuthSubject$.asObservable();
  public readonly azureV2AccessToken$: Observable<string> = this.azureV2Auth$.pipe(
    map((loginResponse: LoginResponse) => {
      return loginResponse.accessToken;
    }),
  );

  // EUA auth response
  private readonly pingAuthSubject$: ReplaySubject<LoginResponse> = new ReplaySubject<LoginResponse>(1);
  public readonly pingAuth$: Observable<LoginResponse> = this.pingAuthSubject$.asObservable();
  public readonly pingAccessToken$: Observable<string> = this.pingAuth$.pipe(
    map((loginResponse: LoginResponse) => {
      return loginResponse.accessToken;
    }),
  );
  private readonly pingUserInfoSubject$: ReplaySubject<EUAV3UserInfo> = new ReplaySubject<EUAV3UserInfo>(1);
  public readonly pingUserInfo$: Observable<EUAV3UserInfo> = this.pingUserInfoSubject$.asObservable();

  loginResponseValue: LoginResponse | undefined;
  pingRefreshToken: string | undefined;

  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly http: HttpClient,
    private readonly config: ConfigService,
  ) {
    this.azureV1Auth$
      .pipe(
        switchMap((loginResponse: LoginResponse) => {
          // get Azure V2 tokens so they can be used with Microsoft Graph APIs
          if (loginResponse?.isAuthenticated) {
            // use Azure V1 refresh token for Azure V2 auth
            return this.oidcSecurityService.getRefreshToken().pipe(
              switchMap((refreshToken: string) => {
                console.log('Call Azure V2 auth');
                return this.getAzureV2Auth(refreshToken);
              }),
            );
          } else {
            console.log('Azure V1 - Not authenticated');
            return of({
              isAuthenticated: false,
            } as LoginResponse);
          }
        }),
      )
      .subscribe((loginResponse: LoginResponse) => {
        if (loginResponse?.isAuthenticated) {
          this.azureV2AuthSubject$.next(loginResponse);
          this.loginResponseValue = loginResponse;
        }
      });

    this.azureV1Auth$
      .pipe(
        switchMap((loginResponse: LoginResponse) => {
          // get Ping tokens so they can be used with serverless APIs
          if (loginResponse?.isAuthenticated) {
            // use Azure V1 ID token for Ping auth
            return this.oidcSecurityService.getIdToken().pipe(
              switchMap((idToken: string) => {
                console.log('Call Ping auth');
                return this.getPingAuth(idToken);
              }),
            );
          } else {
            return of({
              isAuthenticated: false,
            } as LoginResponse);
          }
        }),
      )
      .subscribe((loginResponse: LoginResponse) => {
        this.pingAuthSubject$.next(loginResponse);
        this.loginResponseValue = loginResponse;
      });

    setTimeout(() => {
      //for some reason without the setTimeout the angular oidc auth client can randomly not work.
      this.pingAccessToken$
        .pipe(
          switchMap((token: string) => {
            return this.getPingUserInfo(token);
          }),
        )
        .subscribe((userInfo: EUAV3UserInfo) => {
          this.pingUserInfoSubject$.next(userInfo);
        });
    }, 750);
  }

  checkAuth() {
    // authenticate via Azure V1 so token can be exchanged for an EUA token
    this.oidcSecurityService.checkAuth().subscribe((loginResponse: LoginResponse) => {
      this.azureV1AuthSubject$.next(loginResponse);
      this.loginResponseValue = loginResponse;
    });
  }

  getAzureV2Auth(refreshToken: string): Observable<LoginResponse> {
    const payload = new HttpParams()
      .set('client_id', this.config.app.azureClientID)
      .set('scope', this.config.app.azureScope)
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken);

    return this.http.post(`${this.config.app.azureStsUrl}/${this.config.app.azureNwTenantID}/oauth2/v2.0/token`, payload).pipe(
      map(
        (response: any) =>
          ({
            isAuthenticated: true,
            accessToken: response.access_token,
            idToken: response.id_token,
            userData: jwtDecode(response.id_token),
          }) as LoginResponse,
      ),
      shareReplay(),
      catchError((err: any) => {
        console.log('Error getting Azure V2 tokens', err);
        return of({
          isAuthenticated: false,
          errorMessage: 'Error getting Azure V2 tokens',
        } as LoginResponse);
      }),
    );
  }

  getPingAuth(azureV1IdToken: string): Observable<LoginResponse> {
    const payload = new HttpParams()
      .set('grant_type', 'urn:ietf:params:oauth:grant-type:token-exchange')
      .set('scope', 'openid nwapi')
      .set('subject_token_type', 'urn:ietf:params:oauth:token-type:jwt')
      .set('message_id', crypto.randomUUID())
      .set('subject_token', azureV1IdToken);
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Authorization', 'Basic ' + btoa(this.config.app.clientId + ':'));

    return this.http.post(this.config.app.euaAPIUrl, payload, { headers }).pipe(
      map((response: any) => {
        this.pingRefreshToken = response.refresh_token;
        return {
          isAuthenticated: true,
          accessToken: response.access_token,
        } as LoginResponse;
      }),
      shareReplay(),
      tap(() => {
        // force auth refresh after 15 minutes since Ping tokens are only valid for 20 minutes, but Azure tokens are valid longer
        setTimeout(
          () => {
            if (this.loginResponseValue) {
              this.azureV1AuthSubject$.next(this.loginResponseValue);
            }
          },
          15 * 60 * 1000,
        );
      }),
      catchError((err: any) => {
        if (this.pingRefreshToken) {
          console.log('Error getting Ping tokens, retrying with refresh token', err);
          return this.getPingAuthRefresh(this.pingRefreshToken);
        } else {
          return of({
            isAuthenticated: false,
            errorMessage: 'Error getting Ping tokens',
          } as LoginResponse);
        }
      }),
    );
  }

  getPingUserInfo(token: string): Observable<EUAV3UserInfo> {
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    return this.http.get(this.config.app.euaAPIUserInfoUrl, { headers }).pipe(
      map((response: any) => {
        return response as EUAV3UserInfo;
      }),
      catchError((err: any) => {
        return of({
          lastName: '',
          sub: '',
          manager: '',
          displayName: '',
          groups: [],
          trust_level: '',
          userId: '',
          employeeNumber: '',
          firstName: '',
          employeeType: '',
          scope: [],
          realm: '',
          shortName: '',
          email: '',
        });
      }),
    );
  }

  getPingAuthRefresh(refreshToken: string): Observable<LoginResponse> {
    // clientSecret should never be used in the browser, this workaround can no longer be used
    // const payload = new HttpParams().set('grant_type', 'refresh_token').set('refresh_token', refreshToken);
    // const authValue = `${this.config.app.clientId}:${this.config.app.clientSecret}`;
    // const encodedAuthValue = Buffer.from(authValue).toString('base64');
    // const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded', 'Authorization': 'Basic ' + encodedAuthValue });
    // console.log('ping refresh');
    // return this.http.post(this.config.app.euaAPIRefreshUrl, payload, { headers }).pipe(
    //   map((response: any) => {
    //     this.pingRefreshToken = response.refresh_token;
    //     return {
    //       isAuthenticated: true,
    //       accessToken: response.access_token,
    //       idToken: response.id_token,
    //       refreshToken: response.refresh_token,
    //       userData: jwtDecode(response.id_token),
    //     } as LoginResponse;
    //   }),
    //   shareReplay(),
    //   tap(() => {
    //     // force auth refresh after 15 minutes since Ping tokens are only valid for 20 minutes, but Azure tokens are valid longer
    //     setTimeout(
    //       () => {
    //         if (this.loginResponseValue) {
    //           this.azureV1AuthSubject$.next(this.loginResponseValue);
    //         }
    //       },
    //       15 * 60 * 1000,
    //     );
    //   }),
    //   catchError((err: any) => {
    //     console.log('Error getting Ping tokens', err);
    return of({
      isAuthenticated: false,
      errorMessage: 'Error getting Ping tokens',
    } as LoginResponse);
    //   }),
    // );
  }

  forceRefreshSession(): Observable<LoginResponse> {
    return this.oidcSecurityService.forceRefreshSession();
  }
}
